import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppButton, Box, ShimmerBox, Text } from '@common-fe/common-fe';

import { ROUTES } from '@/common';
import Permissions from '@/common/permissions';
import { Modal } from '@/components';
import { useHasAccess, useHistory } from '@/modules/core/hooks';

import FieldLevelAccessSetupForm from '../FieldLevelAccessSetup/FieldLevelAccessSetupForm';
import useGetLevelAccessByIdQuery from '../queries/useGetLevelAccessById.query';
import { IFieldLevelAccessSetup } from '../role.types';

interface Props {
  id: string;
  onChangeId: (value: string) => void;
  onCopyModalOpen: (value: string) => void;
}
const FieldLevelAccessSummaryModal: React.FC<Props> = ({ id, onChangeId, onCopyModalOpen }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (id) {
      setVisible(true);
    }
  }, [id]);
  const { data, isLoading } = useGetLevelAccessByIdQuery({ id });

  const handleSetVisible = useCallback(
    (value: boolean) => {
      if (!value) {
        onChangeId('');
      }

      setVisible(value);
    },
    [onChangeId]
  );

  const history = useHistory();

  const handleOpenEdit = useCallback(() => {
    history.push(ROUTES.FIELD_LEVEL_ACCESS_SETUP_EDIT(id));
  }, [history, id]);

  const defaultValues = useMemo(() => {
    if (!data) {
      return null;
    }
    const payload: IFieldLevelAccessSetup = {
      ...data,
      name: `${data?.name || ''}`,
      copyLevelAccessId: id,
      isCopyLevelAccess: !!id,
    };
    return payload;
  }, [data, id]);

  const handleOpenCopyModal = useCallback(() => {
    onChangeId('');
    setVisible(false);
    onCopyModalOpen(id);
  }, [id, onChangeId, onCopyModalOpen]);
  const isAvailableToEdit = useHasAccess([
    {
      permission: Permissions.EDIT_ROLES_AND_SECURITY,
    },
  ]);
  return (
    <Modal
      testId="role-permissions-copy"
      visible={visible}
      onSetVisible={handleSetVisible}
      wrapperStyle={{ zIndex: 10000 }}
    >
      <FieldLevelAccessSetupForm
        key={`${data?.id}`}
        defaultValues={defaultValues}
        isLoading={isLoading}
        isModalMode
        isViewMode
        title={
          isLoading ? (
            <ShimmerBox height="32px" width="220px" />
          ) : (
            <Text size="2xl" weight={700} margin={{ bottom: 'l' }} style={{ lineHeight: '32px' }}>
              {data?.name}
            </Text>
          )
        }
        footer={
          isAvailableToEdit ? (
            <Box margin={{ top: 'l' }} direction="row" justify="end">
              <AppButton
                testId="level-access-summary_copy"
                width="160px"
                buttonType="secondary"
                onClick={handleOpenCopyModal}
              >
                Copy Properties
              </AppButton>
              <Box margin={{ left: 's' }}>
                <AppButton
                  testId="level-access-summary_edit"
                  width="160px"
                  buttonType="primary"
                  onClick={handleOpenEdit}
                >
                  Edit
                </AppButton>
              </Box>
            </Box>
          ) : null
        }
      >
      </FieldLevelAccessSetupForm>
    </Modal>
  );
};

export default FieldLevelAccessSummaryModal;
